import React, { useContext, useMemo } from 'react'

import { useApi } from '../../Hooks/useApi'
import { IKvkEnrollment, kvkApi } from '../../Lib/coreApi'

interface IKvkEnrollmentContext {
  enrollment: IKvkEnrollment | undefined
  loading: boolean
  error: Error | undefined
  refetch: () => Promise<void>
  setActiveEnrollment: (enrollmentId: string) => Promise<void>
}

export const KvkEnrollmentContext = React.createContext<IKvkEnrollmentContext>({
  enrollment: undefined,
  loading: true,
  error: undefined,
  refetch: async (): Promise<void> => undefined,
  setActiveEnrollment: async (): Promise<void> => undefined,
})

export const useKvkEnrollment = (): IKvkEnrollmentContext => useContext(KvkEnrollmentContext)

/** Loads the parent's active Koran voor Kids enrollment + child profiles once for the whole app. */
export const KvkEnrollmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, loading, error, refetch } = useApi(() => kvkApi.getEnrollment(), [])

  const value = useMemo(
    () => ({
      enrollment: data,
      loading,
      error,
      refetch,
      // Switches the active enrollment app-wide and reloads the active one.
      setActiveEnrollment: async (enrollmentId: string): Promise<void> => {
        await kvkApi.setActiveEnrollment(enrollmentId)
        await refetch()
      },
    }),
    [data, loading, error, refetch]
  )

  return <KvkEnrollmentContext.Provider value={value}>{children}</KvkEnrollmentContext.Provider>
}
